import React, { useEffect, useState } from "react";
import { Typography, Box, Stack, ToggleButton, ToggleButtonGroup, CircularProgress, TextField, Button } from "@mui/material";
import QuestionnaireDetailCard from "./QuestionnaireDetailCard";
import useReactivateAssignapp from "../hooks/useReactivateAssignapp";
import { ErrorSnackbar } from "../components/Snackbar/ErrorSnackbar";
import useSendTimer from "../hooks/useSendTimer";
import ConfirmationModal from "../components/Modal/ConfirmationModal";
import useUpdateActivationWindow from "../hooks/useUpdateActivationWindow";

const AssignmentSection = ({ assign, appId, refetchUserDetail }) => {
  const hasPreQuestionnaires = Array.isArray(assign.preQuestionnaires) && assign.preQuestionnaires.length > 0;
  const hasInAppQuestionnaires = Array.isArray(assign.inAppQuestionnaires) && assign.inAppQuestionnaires.length > 0;
  const hasPostQuestionnaires = Array.isArray(assign.postQuestionnaires) && assign.postQuestionnaires.length > 0;
  const noQuestionnaires = !hasPreQuestionnaires && !hasInAppQuestionnaires && !hasPostQuestionnaires;

  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Valida la ventana de activación de la asignación
  const currentDate = new Date();
  const activationStartDate = assign.activationWindowStart ? new Date(assign.activationWindowStart) : new Date();
  const activationEndDate = assign.activationWindowEnd ? new Date(assign.activationWindowEnd) : new Date();
  const isWithinActivationWindow = currentDate >= activationStartDate && currentDate <= activationEndDate;

  const shouldStartBlocked = ["Completado", "Tiempo expirado"].includes(assign.status);
  const [selectedState, setSelectedState] = useState(shouldStartBlocked ? "blocked" : "active");
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [loadingAction, setLoadingAction] = useState(false);

  const { reactivateAssignapp, error: reactivateError } = useReactivateAssignapp();
  const { sendTimer, error: sendTimerError } = useSendTimer();

  // Formatea fechas UTC del backend a formato local ISO para el input datetime-local
  const formatToLocalDatetimeInput = (date) => {
    const pad = (n) => String(n).padStart(2, "0");
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const [activationStartInput, setActivationStartInput] = useState(formatToLocalDatetimeInput(activationStartDate));
  const [activationEndInput, setActivationEndInput] = useState(formatToLocalDatetimeInput(activationEndDate));

  const { updateActivationWindow, error: updateWindowError } = useUpdateActivationWindow();
  const [updatingWindow, setUpdatingWindow] = useState(false);
  const [isEditingWindow, setIsEditingWindow] = useState(false);
  const [confirmUpdateWindow, setConfirmUpdateWindow] = useState(false);

  // Maneja el cambio de estado (bloquear o activar)
  const handleStateChange = (_, newValue) => {
    if (!newValue || newValue === selectedState) return;
    if (newValue === "blocked" || newValue === "active") {
      setPendingAction(newValue);
      setOpenConfirmModal(true);
    }
  };

  const performStateChange = async (newValue) => {
    setLoadingAction(true);
    const success = await reactivateAssignapp({ assignappId: assign.id, reactivated: newValue === "active" });
    if (!success) {
      setLoadingAction(false);
      return;
    }
    if (newValue === "blocked") {
      const now = Math.floor(Date.now() / 1000);
      const timerSuccess = await sendTimer({ endTime: now, assignappId: assign.id });
      if (!timerSuccess) {
        setLoadingAction(false);
        return;
      }
    }
    setSelectedState(newValue);
    refetchUserDetail?.();
    setLoadingAction(false);
    setOpenConfirmModal(false);
    setPendingAction(null);
  };

  // Actualiza la ventana de tiempo
  const handleConfirmUpdateWindow = async () => {
    setConfirmUpdateWindow(false);
    setUpdatingWindow(true);
    const success = await updateActivationWindow({
      assignappId: assign.id,
      activationWindowStart: Math.floor(new Date(activationStartInput).getTime() / 1000),
      activationWindowEnd: Math.floor(new Date(activationEndInput).getTime() / 1000),
    });
    if (!success) {
      setUpdatingWindow(false);
      return;
    }
    setIsEditingWindow(false);
    refetchUserDetail?.();
    setUpdatingWindow(false);
  };

  // Maneja los errores de las acciones realizadas
  useEffect(() => {
    const error = reactivateError || sendTimerError || updateWindowError;
    if (error) {
      setErrorMessage(error?.message || "Ocurrió un error inesperado");
      setShowError(true);
    }
  }, [reactivateError, sendTimerError, updateWindowError]);

  return (
    <Box sx={{ mb: 3, p: 2, bgcolor: "grey.50", borderRadius: 1, borderLeft: "4px solid", borderColor: "primary.main" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: "100%" }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="subtitle2" color="text.secondary">
            Aplicación ID: {appId}
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            Asignación ID: {assign.id}
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            Estado: {assign.status}
          </Typography>
        </Stack>
        {isWithinActivationWindow && (
          <ToggleButtonGroup
            value={selectedState}
            exclusive
            onChange={handleStateChange}
            size="small"
            sx={{
              "& .MuiToggleButton-root": {
                px: 2,
                py: 0.5,
                border: "1px solid",
                borderColor: "divider",
                textTransform: "none",
                fontWeight: 500,
                "&:not(.Mui-selected)": { "&:hover": { bgcolor: "action.hover" } },
                "&.Mui-disabled": { opacity: 0.5 },
              },
              "& .MuiToggleButtonGroup-grouped": {
                "&:not(:first-of-type)": { borderLeft: "1px solid", borderColor: "divider" },
              },
            }}
          >
            <ToggleButton
              value="active"
              disabled={loadingAction}
              aria-label="activar asignación"
              sx={{
                "&.Mui-selected": { color: "black", bgcolor: "primary.main", "&:hover": { bgcolor: "primary.light" } },
              }}
            >
              {loadingAction && selectedState === "active" ? <CircularProgress size={20} color="inherit" /> : "Activar"}
            </ToggleButton>
            <ToggleButton
              value="blocked"
              disabled={loadingAction}
              aria-label="bloquear asignación"
              sx={{
                "&.Mui-selected": { color: "white", bgcolor: "#e74040", "&:hover": { bgcolor: "#dd3a3a" } },
              }}
            >
              {loadingAction && selectedState === "blocked" ? <CircularProgress size={20} color="inherit" /> : "Bloquear"}
            </ToggleButton>
          </ToggleButtonGroup>
        )}
      </Stack>

      {/* Ventana de tiempo */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 3, mb: 2 }}>
        Ventana de tiempo:
      </Typography>
      <Box display="flex" gap={1.5} mt={1} alignItems="center" flexWrap="wrap">
        <TextField
          type="datetime-local"
          label="Inicio"
          value={activationStartInput}
          onChange={(e) => setActivationStartInput(e.target.value)}
          size="small"
          disabled={!isEditingWindow}
          sx={{ width: 200, "& input": { padding: "8px 12px", fontSize: 14 } }}
        />
        <TextField
          type="datetime-local"
          label="Fin"
          value={activationEndInput}
          onChange={(e) => setActivationEndInput(e.target.value)}
          size="small"
          disabled={!isEditingWindow}
          sx={{ width: 200, "& input": { padding: "8px 12px", fontSize: 14 } }}
        />
        <Button
          variant="contained"
          disabled={updatingWindow}
          onClick={() => {
            if (!isEditingWindow) {
              setIsEditingWindow(true);
            } else {
              setConfirmUpdateWindow(true);
            }
          }}
          sx={{ textTransform: "none", fontSize: 13, padding: "6px 10px" }}
          startIcon={updatingWindow ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {isEditingWindow ? "Actualizar ventana" : "Editar ventana"}
        </Button>
      </Box>

      {/* Listados de cuestionarios */}
      {noQuestionnaires ? (
        <Box sx={{ textAlign: "center", py: 4, color: "text.secondary", fontStyle: "italic" }}>
          <Typography variant="body2">No hay cuestionarios asignados para esta aplicación</Typography>
        </Box>
      ) : (
        <>
          {hasPreQuestionnaires && (
            <>
              <Typography variant="subtitle2" sx={{ color: "text.secondary", mt: 1.5, mb: 1.5, fontWeight: 500 }}>
                Cuestionarios previos:
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
                  mb: 2,
                }}
              >
                {assign.preQuestionnaires.map((questionnaireItem) => (
                  <QuestionnaireDetailCard
                    key={questionnaireItem.assignapp_questionnaire_id}
                    questionnaireItem={questionnaireItem}
                    refetchUserDetail={refetchUserDetail}
                  />
                ))}
              </Box>
            </>
          )}

          {hasInAppQuestionnaires && (
            <>
              <Typography variant="subtitle2" sx={{ color: "text.secondary", mt: 1.5, mb: 1.5, fontWeight: 500 }}>
                Cuestionarios en aplicación:
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
                  mb: 2,
                }}
              >
                {assign.inAppQuestionnaires.map((questionnaireItem) => (
                  <QuestionnaireDetailCard
                    key={questionnaireItem.assignapp_questionnaire_id}
                    questionnaireItem={questionnaireItem}
                    refetchUserDetail={refetchUserDetail}
                  />
                ))}
              </Box>
            </>
          )}

          {hasPostQuestionnaires && (
            <>
              <Typography variant="subtitle2" sx={{ color: "text.secondary", mt: 1.5, mb: 1.5, fontWeight: 500 }}>
                Cuestionarios posteriores:
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
                }}
              >
                {assign.postQuestionnaires.map((questionnaireItem) => (
                  <QuestionnaireDetailCard
                    key={questionnaireItem.assignapp_questionnaire_id}
                    questionnaireItem={questionnaireItem}
                    refetchUserDetail={refetchUserDetail}
                  />
                ))}
              </Box>
            </>
          )}
        </>
      )}

      {/* Modales y snackbars */}
      <ConfirmationModal
        open={openConfirmModal}
        handleClose={() => setOpenConfirmModal(false)}
        handleConfirm={() => performStateChange(pendingAction)}
        title={pendingAction === "blocked" ? "Bloquear asignación" : "Activar asignación"}
        description={
          pendingAction === "blocked"
            ? "¿Está seguro de que desea bloquear esta asignación para el participante?"
            : "¿Está seguro de que desea activar esta asignación para el participante?"
        }
        confirmText={pendingAction === "blocked" ? "Confirmar bloqueo" : "Confirmar activación"}
        confirmColor={pendingAction === "blocked" ? "error" : "primary"}
      />

      <ConfirmationModal
        open={confirmUpdateWindow}
        handleClose={() => setConfirmUpdateWindow(false)}
        handleConfirm={handleConfirmUpdateWindow}
        title="Actualizar ventana de tiempo"
        description="¿Está seguro de que desea actualizar la ventana de tiempo de esta asignación?"
        confirmText="Confirmar actualización"
        confirmColor="primary"
      />

      <ErrorSnackbar open={showError} onClose={() => setShowError(false)} title="Ha ocurrido un error" message={errorMessage} />
    </Box>
  );
};

export default AssignmentSection;
